/**
 * main-bridge.ts — main-thread façade that owns the worker.
 *
 * `zapd-engine-bridge.js` constructs a `MainThreadBridge` instead of
 * `ZapPlayer`. The bridge:
 *   - spawns the player-worker
 *   - transfers the OffscreenCanvas to it
 *   - posts play/pause/seek/detach commands
 *   - posts AudioScheduler clock updates every 10 ms (master-clock sync)
 *   - receives PCM messages and feeds them to AudioScheduler
 *   - exposes the same public surface that `ZapPlayer` exposed, plus
 *     legacy property access (`frameQueue`, `videoPool`, etc.) backed
 *     by cached snapshots so the existing debug-overlay keeps working
 *     without changes.
 */

// The `?worker&url` Vite suffix bundles the worker source as a
// separate output chunk and provides the runtime URL of the bundle
// as the default export. With `worker.format: 'iife'` in
// vite.player.config.js, the worker is a self-contained classic
// worker — no module-loader required.
import PlayerWorkerUrl from '../worker/player-worker.ts?worker&url';
import {
  assertFreshAsset, maybeReloadForStaleBundle, clearStaleBundleGuard, StaleBundleError,
} from '../bundle-health';

import { AudioScheduler } from './audio-scheduler';
import { PressurePolicy } from '../power/pressure-policy';
import { createDirectIo, type DirectIo } from '../directsource/direct-io';
import type {
  Main2WorkerMsg, Worker2MainMsg, DecodePathInfo, StatsSnapshot,
  DirectProbeStream, DirectProbePacket,
} from '../worker/protocol';

// ── Public types (re-exported from index.ts shim) ───────────────────

/** Result of `MainThreadBridge.directProbe()` (A-P1.0 SAB-AVIO dev probe). */
export interface DirectProbeReport {
  ok:          boolean;
  error?:      string;
  durationUs?: number;
  streams?:    DirectProbeStream[];
  packets?:    DirectProbePacket[];
}

/**
 * Rejection type from `attachDirect()` when the worker answers
 * 'directUnsupported' (unsupported codec / HDR / missing zapd_open_stream on
 * the deployed wasm). The routing layer catches this by name to fall back to
 * HLS silently — it is NOT a user-facing error.
 */
export class DirectUnsupportedError extends Error {
  constructor(public readonly reason: string) {
    super(`direct-play unsupported: ${reason}`);
    this.name = 'DirectUnsupportedError';
  }
}

/**
 * Probe whether the output display can show HDR. Runs on the main thread
 * (the worker has no `matchMedia`/`screen`) and the result is threaded into
 * the WebGPU renderer to gate the EDR-passthrough path.
 *
 * `dynamic-range: high` is the authoritative capability query. A screen-size
 * sanity floor guards the rare case of a browser reporting it on a panel too
 * small to plausibly be a real HDR display (misconfigured emulation) — better
 * to under-claim HDR (browser SDR-tonemap still looks correct) than to push a
 * clamped rgba16float path onto a non-HDR panel.
 */
function probeHdrDisplay(): boolean {
  try {
    if (!self.matchMedia?.('(dynamic-range: high)').matches) return false;
    const scr = (self as unknown as { screen?: { width?: number; height?: number } }).screen;
    const w = scr?.width ?? 0;
    const h = scr?.height ?? 0;
    // A real HDR display is at least laptop-sized; reject sub-640px reports.
    if (w > 0 && h > 0 && (w < 640 || h < 480)) return false;
    return true;
  } catch {
    return false;
  }
}

export interface MainBridgeOptions {
  /** HLS master playlist URL. Optional — unset for a direct-only bridge
   *  (attachDirect); attach() still requires it. */
  manifestUrl?:   string;
  contentId?:     string;
  audioCtx?:      AudioContext;
  skipRenderer?:  boolean;
  /**
   * Direct-mode session-loss recovery (A-P1.1). When a byte-range read hits a
   * 404 (the /stream session was reaped), the bridge invokes this to mint a
   * fresh DirectIo (new sid), then posts 'directReattach'. Return null to give
   * up. Bounded to 3 attempts (native-session._recover pattern).
   */
  onDirectRecover?: () => Promise<{ io: DirectIo; fileSize: number } | null>;
  /**
   * Server-session-loss recovery hook. When the worker reports a 4xx
   * fetch-error during playback (zapd reaped the session, OOM-restart,
   * etc.), the bridge invokes this to obtain a fresh /play/start
   * session URL. Return null to skip recovery. The bridge then sends
   * 'reattachSession' to the worker so playback resumes from the
   * stored time without a visible interruption.
   */
  refreshSession?: () => Promise<string | null>;
}

export interface MainBridgeEvents {
  ready:        () => void;
  buffer:       (endSec: number, aheadSec: number) => void;
  firstFrame:   () => void;
  error:        (e: Error) => void;
  seekStart:    (targetSec: number) => void;
  seekComplete: (targetSec: number) => void;
  ended:        () => void;
  audioSwitchFailed: (reason: string) => void;
}

type Listener<K extends keyof MainBridgeEvents> = MainBridgeEvents[K];

/**
 * Façade backing the legacy `engine.frameQueue` access. The debug
 * overlay reads `.length`, `[0].timestamp`, and `[length-1].timestamp`
 * on this — we emulate those three accessors via a Proxy-like literal.
 */
class FrameQueueFacade {
  constructor(private readonly bridge: MainThreadBridge) {}
  get length(): number { return this.bridge.cachedFrameQueueLen; }
  // The bridge code that reads `frameQueue[0].timestamp` does so as a
  // numeric-index lookup. JS supports indexed-property accessors via Proxy,
  // but the read pattern in zapd-engine-bridge.js is explicit `[0]` and
  // `[len-1]` — we provide them as named getters via Object.defineProperty.
  get '0'(): { timestamp: number } | undefined {
    return this.bridge.cachedFrameQueueLen > 0
      ? { timestamp: this.bridge.cachedFrameQueueHeadTs ?? 0 }
      : undefined;
  }
}

/**
 * Façade backing `engine.videoPool.warmQueueSize` access in the
 * debug overlay.
 */
class VideoPoolFacade {
  constructor(private readonly bridge: MainThreadBridge) {}
  get warmQueueSize(): number { return this.bridge.cachedWarmQueueSize; }
}

/** Façade backing `engine.audioRenderer.currentTimeSec` — live (main-side). */
class AudioRendererFacade {
  constructor(private readonly scheduler: AudioScheduler) {}
  get currentTimeSec(): number { return this.scheduler.currentTimeSec; }
}

// ── MainThreadBridge ─────────────────────────────────────────────────

export class MainThreadBridge {
  private readonly worker: Worker;
  private readonly audioScheduler: AudioScheduler;
  private readonly listeners: Map<keyof MainBridgeEvents, Set<(...a: unknown[]) => void>> = new Map();

  // Cached snapshot — updated by 'stats' postMessage from worker every 200 ms.
  cachedFrameQueueLen     = 0;
  cachedFrameQueueHeadTs: number | null = null;
  cachedFrameQueueTailTs: number | null = null;
  cachedWarmQueueSize     = 0;
  cachedDecodedFrameCount = 0;
  cachedPresentedFrameCount   = 0;
  private pressurePolicy: PressurePolicy | null = null;
  private pressureUnsub: (() => void) | null = null;
  /** Post-seek video wall-anchor (set by 'seekPresented'): lets the UI
   *  clock run from the first presented frame instead of staying pinned
   *  at the seek-target until audio re-anchors (measured 1.8-4s frozen
   *  scrubber). Cleared when audio takes over / on pause / on new seek. */
  private seekVideoAnchorSec  = 0;
  private seekVideoAnchorAtMs = 0;
  cachedDroppedPastCount      = 0;
  cachedDroppedOverflowCount  = 0;
  cachedSkippedFutureCount    = 0;
  cachedBufferEndSec          = 0;
  cachedBandwidthBps          = 0;
  cachedCurrentBitrateBps     = 0;
  cachedVideoWidth:  number | null = null;
  cachedVideoHeight: number | null = null;
  cachedDecodePath: DecodePathInfo = { videoHw: false, videoCodec: 'unknown', audioCodec: null };
  durationSec             = 0;

  // Play/pause state. The legacy <video> element is display:none and never
  // gets a MediaSource (the engine drives a canvas), so its native `paused`
  // flag is permanently true. zapd-engine-bridge.js overrides video.paused
  // with this so the controls-bar toggle (togglePlayPause) reads real state.
  private _paused = true;
  get paused(): boolean { return this._paused; }
  // True between an 'ended' event and the next play()/seek(). Lets play()
  // at end-of-stream restart from the beginning instead of re-anchoring the
  // clock at the end (which would leave the picture frozen).
  private _ended = false;

  // Façades exposed for window.__zapEngine compat.
  readonly frameQueue:    FrameQueueFacade;
  readonly videoPool:     VideoPoolFacade;
  readonly audioRenderer: AudioRendererFacade;
  // Façade-only — actual gate lives in worker (see player-worker.ts).
  // Kept in sync with worker's maxFrameQueueLen for debug-overlay parity.
  readonly maxFrameQueueLen = 720;

  // 10 ms clock-update timer.
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private lastPostedClockSec = NaN;

  // ── Seek-bridge state ─────────────────────────────────────────────
  // Nach `seek(targetSec)` ist audioScheduler.currentTimeSec = 0 bis zum
  // first neuen audio-buffer schedules — kann 5-15 s während seek-encoder-
  // warmup dauern. Während dem würde currentTimeSec=0 returnen, video.
  // currentTime override (in zapd-engine-bridge.js) reads das + UI zeigt
  // position 0, scrubber-thumb springt nach links, controls.js
  // updateTime() snap-back-bug. Plus presentDueFrame's cutoff = 0 → keine
  // frames draining → freeze.
  //
  // pendingSeekTimeSec überbrückt das gap mit wall-clock-extrapolation
  // ab seek-target. Sobald audioScheduler anchored (= currentTimeSec > 0),
  // nehmen wir das (= echte sample-accurate clock). Cleared autom.
  private pendingSeekTimeSec:    number | null = null;
  private pendingSeekStartedAt:  number = 0;

  // ── Audio-rendition-swap clock-bridge ─────────────────────────────
  // Bei `setAudioRendition` (= track-switch ohne seek) wird audio-pipeline
  // 1-2s ausgetauscht. audioScheduler.swap() resettet anchorCtxSec=-1, so
  // dass `audioScheduler.currentTimeSec` für die warmup-zeit 0 returnt.
  // Wenn wir den master-clock-getter dann auf einen FIXED wert pinnten
  // (= alter pendingSeekTimeSec-pfad), kollabiert das clock im worker
  // → buffered video-frames werden als „past" discarded → ruckler beim
  // wieder-anlauf. VLC-pattern (clock.md / clock.c): master-clock advanced
  // weiter via system-time auch wenn der eigentliche master-output (audio)
  // restartet — `system = ts * coeff / rate + offset`. Wir mirroren das
  // mit wallclock-extrapolation: anchor=letzte-bekannte-audio-zeit zum
  // swap-zeitpunkt + `(now - anchorAt)/1000`. Sobald audioScheduler
  // re-anchored (= currentTimeSec > 0), nehmen wir wieder die echte
  // audio-clock. Cleared autom.
  private audioSwapAnchorSec:   number = 0;
  private audioSwapAnchorAtMs:  number = 0;

  // ── Direct-mode (A-P1.1) state ────────────────────────────────────
  /** True once attachDirect's worker answered 'ready' {mode:'direct'}. */
  private directMode = false;
  /** The active DirectIo (SAB plumbing + fetch-worker). Owned here; disposed
   *  on detach and swapped on session-recovery. */
  private io: DirectIo | null = null;
  private directAttachResolve: (() => void) | null = null;
  private directAttachReject:  ((e: Error) => void) | null = null;
  private directRecoveryInFlight = false;
  private directRecoveryAttempts = 0;
  private static readonly kMaxDirectRecoveries = 3;

  // Lifecycle state.
  private detached = false;
  private readyResolved = false;
  private readyResolve: (() => void) | null = null;
  private readyReject:  ((e: Error) => void) | null = null;
  private readonly readyPromise: Promise<void>;

  constructor(private readonly opts: MainBridgeOptions) {
    // Build-stamp so the user can verify they're on the latest bundle.
    // Console-log is intentional + UI-visible — bumped on every fix-cycle.
    // eslint-disable-next-line no-console
    console.log(
      '%c[zapd-engine] worker-mode v2026-05-10-phase11d-diag',
      'background:#1d6;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
    );

    this.audioScheduler = new AudioScheduler({
      audioCtx: opts.audioCtx,
      onError:  (e) => this.emit('error', e),
    });

    this.frameQueue    = new FrameQueueFacade(this);
    this.videoPool     = new VideoPoolFacade(this);
    this.audioRenderer = new AudioRendererFacade(this.audioScheduler);

    this.worker = new Worker(PlayerWorkerUrl, { type: 'classic' });
    this.worker.onmessage = (ev) => this.onWorkerMessage(ev.data as Worker2MainMsg);
    // P0-D: a worker that fails to load (404 / stale bundle) fires onerror but
    // NEVER posts 'ready' — without rejecting readyPromise, attach() hangs in
    // "connecting" forever on a dead canvas. Reject loudly so the UI can recover.
    this.worker.onerror   = (ev) => {
      const e = new Error(ev.message || 'worker failed to load (stale bundle?)');
      this.failReady(e);
      this.emit('error', e);
    };

    this.readyPromise = new Promise<void>((res, rej) => {
      this.readyResolve = res;
      this.readyReject  = rej;
    });

    // Adaptive Drosselung: PressurePolicy (Compute Pressure / Battery /
    // Visibility / reduced-*) → Worker skaliert seinen Decode-Look-Ahead.
    // Fire-and-forget: fehlende APIs lassen die Policy auf 'nominal', ein
    // Fehler hier darf den Player nie blockieren.
    void PressurePolicy.create()
      .then((policy) => {
        if (this.detached) { policy.destroy(); return; }
        this.pressurePolicy = policy;
        const push = () => {
          if (this.detached) return;
          this.post({ type: 'pressureState', level: policy.snapshot.level });
        };
        this.pressureUnsub = policy.on(push);
        push();  // initialen Stand sofort melden
      })
      .catch(() => { /* Policy optional — Player läuft mit 'nominal' */ });
  }

  // ── Public API (parity with ZapPlayer) ─────────────────────────────

  // Reject the pending attach() (readyPromise) exactly once. Used by the worker
  // onerror, the worker-reported 'error', and the readyPromise timeout so a
  // failed warmup surfaces loudly instead of hanging.
  private failReady(e: Error): void {
    if (this.readyResolved || !this.readyReject) return;
    this.readyResolved = true;
    this.readyReject(e);
  }

  async attach(canvas: HTMLCanvasElement): Promise<void> {
    if (this.detached) throw new Error('MainThreadBridge.attach: already detached');

    // P0-D: validate the worker bundle is a real JS payload before we depend on
    // it. A stale/broken deploy can 404 it or serve an HTML SPA-fallback (200),
    // which otherwise loads into a silent dead canvas. On a classified stale
    // bundle, try ONE guarded reload to pick up the fresh deploy; if we already
    // reloaded this session, fail loudly instead of looping.
    try {
      await assertFreshAsset(PlayerWorkerUrl, 'worker');
    } catch (e) {
      if (e instanceof StaleBundleError && maybeReloadForStaleBundle()) {
        // Page is navigating away to reload; block here so attach() never
        // "succeeds" on the doomed bundle.
        await new Promise<never>(() => {});
      }
      throw e;
    }

    // Pre-size the canvas before transfer — OffscreenCanvas takes the
    // dimensions from the time of transfer, and the worker won't have
    // a layout to query.
    const dpr = Math.min(self.devicePixelRatio || 1, 2);
    const cssRect = canvas.getBoundingClientRect();
    canvas.width  = Math.max(1, Math.round((cssRect.width  || canvas.width  || 1920) * dpr));
    canvas.height = Math.max(1, Math.round((cssRect.height || canvas.height || 1080) * dpr));

    const offscreen = canvas.transferControlToOffscreen();
    const cmcdSessionId = `zap-${Math.random().toString(36).slice(2, 12)}-${Date.now()}`;

    this.post({
      type:            'init',
      offscreenCanvas: offscreen,
      manifestUrl:     this.opts.manifestUrl ?? '',
      contentId:       this.opts.contentId ?? 'unknown',
      skipRenderer:    this.opts.skipRenderer ?? false,
      cmcdSessionId,
    }, [offscreen]);

    // Wait for worker 'ready'. Errors during attach reject readyPromise; a hung
    // warmup (wasm fetch wedged, worker never replies) would otherwise spin
    // forever, so cap it with a 15s budget that rejects loudly (P0-D).
    let readyTimer: ReturnType<typeof setTimeout> | undefined;
    const readyTimeout = new Promise<never>((_, rej) => {
      readyTimer = setTimeout(() => {
        const e = new Error('worker did not signal ready within 15s (wasm/worker load wedged?)');
        this.failReady(e);
        rej(e);
      }, 15000);
    });
    try {
      await Promise.race([this.readyPromise, readyTimeout]);
    } finally {
      if (readyTimer !== undefined) clearTimeout(readyTimer);
    }
    // Warmup fully succeeded → allow a future stale deploy to earn one reload.
    clearStaleBundleGuard();
  }

  /**
   * Browser-DirectPlay attach (A-P1.1). Transfers the canvas + hands the
   * DirectIo's SABs (structured-clone, NOT transferred) to the worker's
   * initDirect path. Resolves on 'ready' {mode:'direct'} or rejects with a
   * DirectUnsupportedError so the caller falls back to HLS. The bridge takes
   * ownership of `io` (disposed on detach / swapped on recovery).
   */
  async attachDirect(canvas: HTMLCanvasElement, spec: {
    io:                DirectIo;
    fileSize:          number;
    audioStreamIndex?: number;
  }): Promise<void> {
    if (this.detached) throw new Error('MainThreadBridge.attachDirect: already detached');

    try {
      await assertFreshAsset(PlayerWorkerUrl, 'worker');
    } catch (e) {
      if (e instanceof StaleBundleError && maybeReloadForStaleBundle()) {
        await new Promise<never>(() => {});
      }
      throw e;
    }

    this.io = spec.io;

    const dpr = Math.min(self.devicePixelRatio || 1, 2);
    const cssRect = canvas.getBoundingClientRect();
    canvas.width  = Math.max(1, Math.round((cssRect.width  || canvas.width  || 1920) * dpr));
    canvas.height = Math.max(1, Math.round((cssRect.height || canvas.height || 1080) * dpr));
    const offscreen = canvas.transferControlToOffscreen();

    const attachPromise = new Promise<void>((resolve, reject) => {
      this.directAttachResolve = resolve;
      this.directAttachReject  = reject;
    });

    // HDR-display probe: only main can run matchMedia (the worker has no DOM),
    // so measure here and hand the result to the renderer via the worker. Screen
    // heuristic guards against browsers that report dynamic-range:high on a
    // clearly-SDR panel (implausibly small screen = not a real HDR display).
    const hdrDisplay = probeHdrDisplay();

    // SABs are structured-clone-shared (NOT transferred) — the fetch-worker
    // holds its own references, and structured-clone of a SAB shares memory.
    this.post({
      type:             'initDirect',
      offscreenCanvas:  offscreen,
      contentId:        this.opts.contentId ?? 'unknown',
      skipRenderer:     this.opts.skipRenderer ?? false,
      ctrlSab:          spec.io.ctrlSab,
      dataSab:          spec.io.dataSab,
      fileSize:         spec.fileSize,
      audioStreamIndex: spec.audioStreamIndex,
      hdrDisplay,
    }, [offscreen]);

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, rej) => {
      timer = setTimeout(() => {
        const e = new DirectUnsupportedError('timeout: worker did not signal ready within 15s');
        this.failDirectAttach(e);
        rej(e);
      }, 15000);
    });
    try {
      await Promise.race([attachPromise, timeout]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      this.directAttachResolve = null;
      this.directAttachReject  = null;
    }
    clearStaleBundleGuard();
  }

  private failDirectAttach(e: Error): void {
    const reject = this.directAttachReject;
    this.directAttachResolve = null;
    this.directAttachReject  = null;
    reject?.(e);
  }

  async play(): Promise<void> {
    this._paused = false;
    // Play after end-of-stream = restart from the beginning (YouTube/Plex
    // behaviour). Without this, play() at EOS re-anchors the clock at the end
    // and the picture stays frozen while the button flips to "pause".
    if (this._ended) {
      this._ended = false;
      this.seek(0);
    }
    // AudioContext.resume must come from a user-gesture; play() is
    // typically called from a click handler so this is the right spot.
    await this.audioScheduler.start();
    // 2026-05-08: if no frame has been presented yet, this is a fresh
    // start (= not resume-from-pause). The audioScheduler may have a
    // stale anchor from background activity during warmup (= ctx.
    // currentTime advanced while waiting on segments → currentTimeSec
    // reads minutes ahead of actual buffer position). Swap to clear.
    // First post-play audio buffer will re-anchor at the correct PTS.
    if (this.cachedPresentedFrameCount === 0) {
      this.audioScheduler.swap();
      this.pendingSeekTimeSec = 0;
    }
    this.startClockTimer();
    this.post({ type: 'play' });
  }

  pause(): void {
    this._paused = true;
    this.seekVideoAnchorSec = 0;  // frozen UI falls back to the seek-pin
    this.audioScheduler.pause();
    this.stopClockTimer();
    this.post({ type: 'pause' });
  }

  seek(targetSec: number): void {
    this._ended = false;   // a seek un-ends the stream
    // Direct-mode: unblock a possibly-hanging byte-range read BEFORE the worker
    // processes the seek (its read-callback may be parked in Atomics.wait). Main
    // does NOT resetAbort — the worker clears ABORT itself inside directOrch.reset
    // (or the in-buffer fast path), so there is no clear-vs-seek race.
    if (this.directMode && this.io) {
      this.io.abort();
    }
    // Bridge-clock-anchor: until audioScheduler anchors at the new PTS
    // (= 5-15s seek-warmup), currentTimeSec getter falls back to this
    // value + elapsed wallclock. Without it, video.currentTime returns 0
    // mid-seek → UI thinks playhead jumped to start.
    const target = Math.max(0, targetSec);
    this.pendingSeekTimeSec   = target;
    this.seekVideoAnchorSec   = 0;  // new seek → wait for its own seekPresented
    this.pendingSeekStartedAt = performance.now();
    this.audioScheduler.swap();
    // Sync-fix 2026-05-11: prime the scheduler with the seek-target so
    // any STALE pre-seek audio buffers (= already in-flight from worker
    // when seek fired) get dropped instead of anchoring the master-clock
    // at the OLD playback position. Without this, the first post-swap
    // buffer's PTS (=pre-seek ~1460s) wins the anchor → currentTimeSec
    // returns 1460 instead of 3184 → video selects pre-seek frames →
    // audio plays new content but video is locked to old position →
    // "nicht in sync nach seek". setNextAnchor with target ≫ stale-PTS
    // makes the (a)>=bufEnd branch fire in scheduleNode → buffer dropped.
    if (target > 0) {
      this.audioScheduler.setNextAnchor(target, 'seek');
    }
    this.post({ type: 'seek', targetSec });
  }

  /**
   * Switch audio rendition on-the-fly without restarting the player.
   * Pass the new rendition URL (= one of master.m3u8's EXT-X-MEDIA URIs).
   * Video continues uninterrupted; only audio fetches + decoder + scheduler
   * swap. Resumes at `resumeSec` if provided (= video.currentTime), sonst
   * current audio-scheduler-time.
   *
   * Phase 8: resumeSec MUSS dem `position` entsprechen das beim server-side
   * /switch-call mitgesendet wurde, damit client + server denselben
   * start-seg verwenden. Sonst lagert audio-clock hinter video-clock und
   * client fetcht segs die der server nie produziert (= 503-loop).
   */
  setAudioRendition(url: string, resumeSec?: number): void {
    const target = (typeof resumeSec === 'number' && resumeSec > 0)
                 ? resumeSec
                 : this.audioScheduler.currentTimeSec;
    // VLC-pattern: master-clock advanced via wallclock-extrapolation
    // während audio-pipeline ausgetauscht wird. NICHT pendingSeekTimeSec
    // — das wäre ein seek-pin (= clock frozen, video-frames discarded).
    // Audio-swap soll für video unsichtbar sein: clock advanced normal,
    // buffered video-frames werden weiter presentiert.
    if (target > 0) {
      this.audioSwapAnchorSec  = target;
      this.audioSwapAnchorAtMs = performance.now();
    }
    this.audioScheduler.swap();
    // Audio-swap sync-fix S4 (2026-05-10): tell scheduler where
    // master-clock currently is. The first new buffer that arrives will
    // anchor at this target (= matches main-bridge's wall-clock-
    // extrapolation), and play with offsetSec into the buffer to avoid
    // a backwards clock-jump up to seg-boundary distance (= 4s).
    if (target > 0) {
      this.audioScheduler.setNextAnchor(target);
    }
    this.post({ type: 'setAudioRendition', url, resumeSec: target });
  }

  /**
   * Direct-mode audio-track switch (A-P1.1). `streamIndex` is the GLOBAL libav
   * source-stream index (same space as the SPA's ordinal→stream resolve).
   * Video keeps running; only the worker's audio decoder + orchestrator repoint.
   */
  setDirectAudioTrack(streamIndex: number): void {
    if (!this.directMode) return;
    // Re-anchor the master clock across the audio-decoder swap so buffered video
    // keeps presenting (VLC-pattern, same as setAudioRendition's swap-gap).
    const target = this.audioScheduler.currentTimeSec;
    if (target > 0) {
      this.audioSwapAnchorSec  = target;
      this.audioSwapAnchorAtMs = performance.now();
      this.audioScheduler.swap();
      this.audioScheduler.setNextAnchor(target);
    } else {
      this.audioScheduler.swap();
    }
    this.post({ type: 'directSetAudioTrack', streamIndex });
  }

  /** Which pipeline is live — 'direct' (A-P1.1) or 'hls'. For the debug overlay. */
  getMode(): 'direct' | 'hls' { return this.directMode ? 'direct' : 'hls'; }

  /** Frame-step during pause. +N forward, -N backward. */
  /** Debug-overlay visibility → worker stats cadence (200ms vs 2s). */
  setStatsEnabled(enabled: boolean): void {
    this.post({ type: 'setStatsEnabled', enabled });
  }

  frameStep(frames: number): void {
    this.post({ type: 'frameStep', frames });
  }

  detach(): void {
    if (this.detached) return;
    this.detached = true;
    if (this.pressureUnsub) { this.pressureUnsub(); this.pressureUnsub = null; }
    if (this.pressurePolicy) { this.pressurePolicy.destroy(); this.pressurePolicy = null; }
    this.stopClockTimer();
    this.post({ type: 'detach' });
    // Direct-mode: tear down the SAB plumbing + fetch-worker.
    if (this.io) { try { this.io.dispose(); } catch { /* noop */ } this.io = null; }
    // Schedule worker.terminate after 'shutdown-ack' or after a
    // 1 s timeout, whichever comes first.
    setTimeout(() => { try { this.worker.terminate(); } catch { /* noop */ } }, 1000);
    this.audioScheduler.close();
  }

  /** Master media-clock — used by bridge.js for `video.currentTime` mirror
   *  AND posted to worker via clockUpdate every 10 ms.
   *
   *  2026-05-08 v2: PIN at seek-target during warmup, don't advance.
   *  Earlier version returned seek-target + elapsed-wallclock — but that
   *  caused drop-past-gate in worker to discard post-seek frames at
   *  PTS=target once 1+ second of warmup had elapsed (frame.ts < ctUs-1
   *  ⇒ target < (target+elapsed)-1 ⇒ true ⇒ drop). All post-seek
   *  decoder output got dropped before reaching frameQueue.
   *
   *  Now: returns pendingSeekTimeSec UNCHANGED until audio anchors at
   *  the new PTS (audioScheduler.currentTimeSec > 0). Once audio takes
   *  over, the real audio-clock advances normally. Picture-presentation
   *  during warmup is OK because nothing's playing yet anyway. */
  get currentTimeSec(): number {
    const audioTime = this.audioScheduler.currentTimeSec;
    if (audioTime > 0) {
      // Audio anchored — use it (= sample-accurate). Clear all fallbacks.
      if (this.pendingSeekTimeSec !== null) this.pendingSeekTimeSec = null;
      this.audioSwapAnchorSec  = 0;
      this.seekVideoAnchorSec  = 0;
      return audioTime;
    }
    // Seek-pin: pinned at seek-target (no elapsed) so worker's
    // drop-past-gate doesn't discard fresh post-seek frames at PTS=target.
    // Used during seek warmup (= different semantics from audio-swap).
    // Once the first post-seek frame is PRESENTED ('seekPresented'), the
    // pin is replaced by a wall-clock advance from that frame's PTS: the
    // picture is provably flowing, so the drop-past concern is gone and
    // the UI/scrubber must live NOW, not when audio lands. Audio still
    // wins the moment it anchors (sample-accurate, minor sub-100ms nudge).
    if (this.pendingSeekTimeSec !== null) {
      if (this.seekVideoAnchorSec > 0 && !this._paused) {
        return this.seekVideoAnchorSec
             + (performance.now() - this.seekVideoAnchorAtMs) / 1000;
      }
      return this.pendingSeekTimeSec;
    }
    // Audio-rendition-swap-gap: advance via wallclock-rate from pre-swap
    // audio-time so buffered video continues presenting smoothly. VLC's
    // master-clock-pattern: system-time advancement is independent of
    // slave-output restart.
    if (this.audioSwapAnchorSec > 0) {
      return this.audioSwapAnchorSec
           + (performance.now() - this.audioSwapAnchorAtMs) / 1000;
    }
    return 0;
  }

  getDecodePath(): DecodePathInfo { return { ...this.cachedDecodePath }; }
  getDecodedFrameCount(): number  { return this.cachedDecodedFrameCount; }
  getPresentedFrameCount(): number { return this.cachedPresentedFrameCount; }
  getDroppedCounts(): { past: number; overflow: number; future: number } {
    return {
      past:     this.cachedDroppedPastCount,
      overflow: this.cachedDroppedOverflowCount,
      future:   this.cachedSkippedFutureCount,
    };
  }
  getBufferEndSec(): number     { return this.cachedBufferEndSec; }
  getBandwidthBps(): number     { return this.cachedBandwidthBps; }
  getCurrentBitrateBps(): number { return this.cachedCurrentBitrateBps; }
  getVideoSize(): { width: number | null; height: number | null } {
    return { width: this.cachedVideoWidth, height: this.cachedVideoHeight };
  }

  on<K extends keyof MainBridgeEvents>(event: K, listener: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (!set) { set = new Set(); this.listeners.set(event, set); }
    set.add(listener as (...a: unknown[]) => void);
    return () => { set!.delete(listener as (...a: unknown[]) => void); };
  }

  // ── Worker message handling ────────────────────────────────────────

  private onWorkerMessage(msg: Worker2MainMsg): void {
    switch (msg.type) {
      case 'ready':
        this.cachedDecodePath = msg.decodePath;
        this.durationSec      = msg.durationSec;
        if (msg.mode === 'direct') {
          this.directMode = true;
          const resolve = this.directAttachResolve;
          this.directAttachResolve = null;
          this.directAttachReject  = null;
          resolve?.();
        }
        this.readyResolved    = true;
        this.readyResolve?.();
        this.emit('ready');
        break;

      case 'pcm':
        // PCM channels were transferred — we own them now.
        this.audioScheduler.scheduleBuffer(msg.ptsUs, msg.channels, msg.sampleRate);
        break;

      case 'firstFrame':
        this.emit('firstFrame');
        break;

      case 'ended':
        // EOS = paused (sonst liest der video.paused-Override false am Stream-
        // Ende → togglePlayPause naehme den pause-Zweig statt neu zu starten,
        // und wasPlaying-Checks lesen EOS faelschlich als "spielt").
        this._paused = true;
        this._ended  = true;   // play() restarts from 0 next
        // Energie: der Worker hat seinen Render-Loop schon gestoppt; ohne
        // die zwei Stopps hier laufen der 10ms-Clock-Feed (ctx.currentTime
        // steigt weiter → Delta-Guard greift nicht) und der AudioContext
        // auf dem Endscreen unbegrenzt weiter. play() nach EOS ruft
        // audioScheduler.start() + startClockTimer() ohnehin wieder auf.
        this.stopClockTimer();
        this.audioScheduler.pause();
        this.emit('ended');
        break;

      case 'buffer':
        this.emit('buffer', msg.bufferEndSec, msg.aheadSec);
        break;

      case 'seekStart':
        this.emit('seekStart', msg.targetSec);
        break;

      case 'seekPresented':
        this.seekVideoAnchorSec  = msg.ptsSec;
        this.seekVideoAnchorAtMs = performance.now();
        break;

      case 'seekComplete':
        this.emit('seekComplete', msg.targetSec);
        break;

      case 'error': {
        const e = new Error(msg.message) as Error & { transient?: boolean };
        if (msg.stack) e.stack = msg.stack;
        if (msg.transient) e.transient = true;
        if (!this.readyResolved && this.readyReject) {
          // Reject the attach() promise.
          this.readyResolved = true;
          this.readyReject(e);
        }
        this.emit('error', e);
        break;
      }

      case 'stats':
        this.applyStats(msg.snapshot);
        break;

      case 'stall': {
        // Worker has had no decoder progress for ≥2s while playing.
        // Surface as a console-warning-tagged error so the user can
        // see the diagnostic state when the canvas freezes.
        const e = new Error(
          `[zapd-engine] STALL after ${msg.stalledForMs}ms — ` +
          `decodedFrames=${msg.decodedFrameCount}, ` +
          `warmQ=${msg.warmQueueSize}, ` +
          `frameQueueLen=${msg.frameQueueLen}, ` +
          `lastFrameTs=${msg.lastFrameTs}, ` +
          `orchestratorIdx=${msg.orchestratorIdx}, ` +
          `currentTime=${msg.currentTimeSec.toFixed(2)}s`
        );
        this.emit('error', e);
        break;
      }

      case 'gpuLost': {
        const e = new Error(
          `[zapd-engine] WebGPU device LOST: ${msg.reason} ${msg.message}`.trim()
        );
        this.emit('error', e);
        break;
      }

      case 'sessionLost':
        void this.recoverSession(msg.currentTimeSec, msg.reason);
        break;

      case 'directUnsupported':
        // Fires ONLY before ready → reject attachDirect so routing does HLS.
        this.failDirectAttach(new DirectUnsupportedError(msg.reason));
        break;

      case 'directSessionLost':
        void this.recoverDirectSession(msg.currentTimeSec, msg.reason);
        break;

      case 'audioSwitchFailed':
        this.emit('audioSwitchFailed', msg.reason);
        break;

      case 'shutdown-ack':
        try { this.worker.terminate(); } catch { /* noop */ }
        break;

      case 'directProbeResult': {
        const resolve = this.directProbePending;
        this.directProbePending = null;
        resolve?.({
          ok:         msg.ok,
          error:      msg.error,
          durationUs: msg.durationUs,
          streams:    msg.streams,
          packets:    msg.packets,
        });
        break;
      }
    }
  }

  private applyStats(s: StatsSnapshot): void {
    this.cachedFrameQueueLen     = s.frameQueueLen;
    this.cachedFrameQueueHeadTs  = s.frameQueueHeadTs;
    this.cachedFrameQueueTailTs  = s.frameQueueTailTs;
    this.cachedWarmQueueSize     = s.warmQueueSize;
    this.cachedDecodedFrameCount      = s.decodedFrameCount;
    this.cachedPresentedFrameCount    = s.presentedFrameCount;
    this.cachedDroppedPastCount       = s.droppedPastCount;
    this.cachedDroppedOverflowCount   = s.droppedOverflowCount;
    this.cachedSkippedFutureCount     = s.skippedFutureCount;
    this.cachedBufferEndSec           = s.bufferEndSec;
    this.cachedBandwidthBps           = s.observedBandwidthBps;
    this.cachedCurrentBitrateBps      = s.currentVariantBitrateBps;
    this.cachedVideoWidth             = s.videoWidth;
    this.cachedVideoHeight            = s.videoHeight;
    this.cachedRendererState          = s.rendererState ?? 'na';
    this.cachedRafTickCount           = s.rafTickCount;
  }
  cachedRendererState: 'ok' | 'null' | 'failed' | 'na' = 'na';
  cachedRafTickCount = 0;

  /** Render-scheduler wakeups since attach (content-rate pacing metric). */
  getRafTickCount(): number { return this.cachedRafTickCount; }

  // ── A-P1.0 SAB-AVIO plumbing (dev-only probe) ───────────────────────
  //
  // Reachable via `window.__zapEngine.directProbe(...)`. NOT wired into
  // playback/routing (that is A-P1.1). Exercises the full SAB-AVIO path end to
  // end: allocates the SABs + spawns the fetch-worker (createDirectIo), hands
  // the SABs to the player-worker, opens the ORIGINAL container over byte-range
  // (zapd_open_stream), and returns StreamInfo + first packet metas so it can
  // be diffed against the MEMFS open. The DirectSource orchestrator replaces
  // this with a real pull-loop and reuses createDirectIo().
  private directProbePending: ((r: DirectProbeReport) => void) | null = null;

  async directProbe(opts: {
    url:         string;
    fileSize:    number;
    token?:      string | null;
    credentials?: RequestCredentials;
    maxPackets?: number;
    timeoutMs?:  number;
  }): Promise<DirectProbeReport> {
    if (this.detached) throw new Error('directProbe: bridge detached');
    if (this.directProbePending) throw new Error('directProbe: already in flight');

    const io: DirectIo = createDirectIo({
      url:         opts.url,
      fileSize:    opts.fileSize,
      token:       opts.token,
      credentials: opts.credentials,
    });

    try {
      return await new Promise<DirectProbeReport>((resolve, reject) => {
        const timeoutMs = opts.timeoutMs ?? 20000;
        const timer = setTimeout(() => {
          if (this.directProbePending !== onResult) return;
          this.directProbePending = null;
          reject(new Error('directProbe: timeout'));
        }, timeoutMs);
        const onResult = (r: DirectProbeReport): void => {
          clearTimeout(timer);
          resolve(r);
        };
        this.directProbePending = onResult;
        // SABs are structured-clone-shared (NOT transferred).
        this.post({
          type:       'directProbe',
          ctrlSab:    io.ctrlSab,
          dataSab:    io.dataSab,
          fileSize:   io.fileSize,
          maxPackets: opts.maxPackets ?? 50,
        });
      });
    } finally {
      io.dispose();
    }
  }

  // Server-session-loss recovery — see MainBridgeOptions.refreshSession.
  private recoveryInFlight = false;

  private async recoverSession(resumeAtSec: number, reason: string): Promise<void> {
    if (this.recoveryInFlight || this.detached) return;
    this.recoveryInFlight = true;
    // 2026-05-08: diagnostic to track WHY recovery fires (= jump-back-2-3s
    // in user's UX). The `reason` comes from the original error message
    // (e.g. "buffer-orchestrator: HTTP 404 for ...m4s"). Logging at
    // console.warn so it stands out in DevTools.
    console.warn(
      `[zapd-engine] session-recovery firing at ${resumeAtSec.toFixed(2)}s: ${reason}`,
    );
    try {
      if (!this.opts.refreshSession) {
        // No callback — surface as plain error so the caller can show
        // the existing attach-error overlay.
        this.emit('error', new Error(
          `[zapd-engine] server session lost (${reason}) and no refreshSession callback configured`,
        ));
        return;
      }
      const newUrl = await this.opts.refreshSession();
      if (!newUrl) {
        this.emit('error', new Error(`[zapd-engine] session-recovery returned no URL (${reason})`));
        return;
      }
      // Reset audio anchor — old anchorPtsSec belongs to dead session's
      // PTS-domain. Next PCM from the new session re-anchors.
      this.audioScheduler.swap();
      this.post({ type: 'reattachSession', manifestUrl: newUrl, resumeAtSec });
    } catch (e) {
      this.emit('error', e instanceof Error ? e : new Error(String(e)));
    } finally {
      this.recoveryInFlight = false;
    }
  }

  /**
   * Direct-mode session recovery (A-P1.1). A byte-range read hit a 404 (the
   * /stream session was reaped). Ask the host for a fresh sid + DirectIo, adopt
   * it, and post 'directReattach' so the worker re-opens and seeks back.
   * Bounded to kMaxDirectRecoveries (native-session._recover pattern) — no
   * server keepalive is needed; the range-fetches ARE the liveness signal, and
   * a >5min pause parks the encoder → reaper → this path covers the resume.
   */
  private async recoverDirectSession(resumeAtSec: number, reason: string): Promise<void> {
    if (this.directRecoveryInFlight || this.detached) return;
    this.directRecoveryInFlight = true;
    if (this.directRecoveryAttempts >= MainThreadBridge.kMaxDirectRecoveries) {
      this.directRecoveryInFlight = false;
      this.emit('error', new Error(`[zapd-engine] direct session-recovery gave up after ${this.directRecoveryAttempts} attempts (${reason})`));
      return;
    }
    this.directRecoveryAttempts++;
    console.warn(`[zapd-engine] direct session-recovery #${this.directRecoveryAttempts} at ${resumeAtSec.toFixed(2)}s: ${reason}`);
    try {
      if (!this.opts.onDirectRecover) {
        this.emit('error', new Error(`[zapd-engine] direct session lost (${reason}) and no onDirectRecover configured`));
        return;
      }
      const fresh = await this.opts.onDirectRecover();
      if (!fresh) {
        this.emit('error', new Error(`[zapd-engine] direct session-recovery returned no session (${reason})`));
        return;
      }
      const old = this.io;
      this.io = fresh.io;
      if (old) { try { old.dispose(); } catch { /* noop */ } }
      // Reset the audio anchor — the old PTS-domain is dead; next PCM re-anchors.
      this.audioScheduler.swap();
      this.post({
        type:        'directReattach',
        ctrlSab:     fresh.io.ctrlSab,
        dataSab:     fresh.io.dataSab,
        fileSize:    fresh.fileSize,
        resumeAtSec,
      });
    } catch (e) {
      this.emit('error', e instanceof Error ? e : new Error(String(e)));
    } finally {
      this.directRecoveryInFlight = false;
    }
  }

  // ── Clock-update loop ──────────────────────────────────────────────

  private startClockTimer(): void {
    if (this.clockTimer !== null) return;
    this.clockTimer = setInterval(() => {
      if (this.detached) return;
      // Delta-Guard: der Timer tickt alle 10ms, aber currentTimeSec steht
      // still bei Pause/Seek-Warmup → sonst 100 identische postMessages/s.
      // Nur posten wenn sich der Clock-Wert tatsaechlich bewegt hat.
      const t = this.audioScheduler.currentTimeSec;
      if (t === this.lastPostedClockSec) return;
      this.lastPostedClockSec = t;
      this.post({ type: 'clockUpdate', currentTimeSec: t });
    }, 10);
  }

  private stopClockTimer(): void {
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────

  private post(msg: Main2WorkerMsg, transfer: Transferable[] = []): void {
    if (this.detached && msg.type !== 'detach') return;
    this.worker.postMessage(msg, transfer);
  }

  private emit<K extends keyof MainBridgeEvents>(event: K, ...args: Parameters<MainBridgeEvents[K]>): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of set) {
      try { (fn as (...a: unknown[]) => void)(...(args as unknown[])); }
      catch { /* never let a listener-throw break the bridge */ }
    }
  }
}
